import Vue, { PluginObject } from "vue";
import { Location, Route } from 'vue-router';
import { Store } from 'vuex';
import AuthService, { AuthModel, TokenModel } from '@/services/core/AuthService';

interface AuthState
{
    loaded: boolean;
    authenticated: boolean;
    identity: AuthModel;
    impersonated: number;
}

interface Auth
{
    login(username: string, password: string, rememberMe?: boolean, postLoginCheck?: (token: any) => boolean): void;
    logout(redirect?: Location): void;
    token(): string;
    fetch(): void;
    user(): AuthModel;
    check(): boolean;
    ready(): boolean;
    reload(): void;
    impersonating(): number;
    impersonate(id: number): void;
    unimpersonate(): void;
}

interface AuthRedirect
{
    homePage: Location;
    loginPage: Location;
    forbidden: Location;
    notFound: Location;
    changePassword: Location;
}

export class AuthOptions
{
    public store: Store<any>;
    public routes: AuthRedirect;
}

export class AuthHelper implements Auth
{
    private router: any;
    private store: Store<any>;
    private options: AuthOptions;

    public constructor(router: any, options: AuthOptions)
    {
        this.router = router;
        this.store = options.store;
        this.options = options;

        this.initStore();
        this.initRouter();
        this.initSession();
        this.initTokens();
        this.initImpersonation();
    }

    private initStore(): void
    {
        this.store.registerModule('auth', {
            namespaced: true,
            state: {
                loaded: false,
                authenticated: false,
                identity: null,
                impersonated: null
            },
            mutations: {
                setLoaded(state, value: boolean)
                {
                    state.loaded = value;
                },
                setAuthenticated(state, value: boolean)
                {
                    state.authenticated = value;
                },
                setIdentity(state, identity: AuthModel)
                {
                    state.identity = identity;
                },
                clearIdentity(state)
                {
                    state.identity = null;
                },
                impersonate(state, id: number)
                {
                    state.impersonated = id;
                },
                unimpersonate(state)
                {
                    state.impersonated = null;
                }
            }
        }, {
            preserveState: false
        });
    }

    private initRouter(): void
    {
        this.router.beforeEach(async (to: Route, from: Route, next: any) =>
        {
            if (this.state.loaded === false)
            {
                // Usunięcie access tokena jeśli wygasł
                if (this.accessToken.length > 0)
                {
                    let payload = this.decryptToken(this.accessToken);
                    let timeLeft = Number(payload.exp) * 1000  - (new Date()).getTime();

                    if (timeLeft <= 0)
                    {
                        this.unimpersonate();
                        this.clearAccessToken();
                    }
                }

                // Uzyskanie nowego access tokena za pomocą refresh tokena (opcja "remember me")
                if (this.accessToken.length === 0 && this.refreshToken.length > 0)
                {
                    try
                    {
                        let result = await AuthService.recoverToken(this.refreshToken);
                        this.setTokens(result, true);
                    }
                    catch (ex)
                    {
                        this.clearTokens();
                    }
                }
                // Logowanie Windows
                if (process.env.VUE_APP_WIN_AUTH == "true" && this.accessToken.length === 0)
                {
                    try
                    {
                        let result = await AuthService.ntlm();
                        this.setTokens(result, false);
                    }
                    catch (ex)
                    {
                        this.clearTokens();
                    }
                }

                // Pobranie danych użytkownika za pomocą access tokena
                if (this.accessToken.length > 0)
                {
                    await this.fetch();
                }

                this.store.commit('auth/setLoaded', true);
            }

            next();
        });

        this.router.beforeEach(async (to: Route, from: Route, next: any) =>
        {
            if (to.meta &&  to.meta.hasOwnProperty('auth'))
            {
                let auth: any = to.meta.auth;

                // Wymuszenie zmiany hasła
                if (auth !== false && this.state.authenticated === true && this.state.identity.forcePasswordChange === true && to.meta.stop !== true)
                {
                    Vue.alert.info(Vue.i18n.$t('[[[Passordet ditt er utløpt. Endre dem til nye.]]]'));
                    next(this.options.routes.changePassword);
                    return;
                }

                if (typeof(auth) === "boolean")
                {
                    if (auth === true && this.state.authenticated === false)
                    {
                        next(this.options.routes.loginPage);
                        return;
                    }
                    if (auth === false && this.state.authenticated === true)
                    {
                        next(this.options.routes.homePage);
                        return;
                    }
                }

                if (Array.isArray(auth) && auth.length > 0)
                {
                    if (this.state.authenticated === false)
                    {
                        next(this.options.routes.loginPage);
                        return;
                    }
                    else if (this.state.identity.permissions.every(p => auth.indexOf(p) >= 0) === false)
                    {
                        next(this.options.routes.forbidden);
                        return;
                    }
                }
            }

            next();
        });
    }

    private initSession(): void
    {
        const sessionLifetime = 60; // minuty
        const activityInterval = 5; // sekundy

        document.body.addEventListener('click', () => this.lastActivity = Date.now());

        this.lastActivity = Date.now();

        setInterval(() =>
        {
            // Z włączoną opcją "remember me" sesja nigdy nie wygasa
            if (this.refreshToken.length === 0)
            {
                const timeLeft = (this.lastActivity + sessionLifetime * 60 * 1000) - Date.now();

                if (timeLeft < 0 && this.check())
                {
                    this.logout();
                }
            }
        },
        activityInterval * 1000);
    }

    private initTokens(): void
    {
        let tokens: TokenModel = {
            token: this.accessToken,
            refresh: this.refreshToken,
            expires: null
        };

        this.setTokens(tokens, tokens.refresh.length > 0);
    }

    private initImpersonation(): void
    {
        var impersonated = localStorage.getItem('impersonated');

        if (impersonated)
        {
            this.store.commit('auth/impersonate', Number(impersonated));
        }

        window.addEventListener('storage', (e) =>
        {
            if (e.key === 'impersonated')
            {
                if (e.newValue != null)
                    this.store.commit('auth/impersonate', Number(e.newValue));
                else
                    this.store.commit('auth/unimpersonate');
            }
        });
    }

    private get state(): AuthState
    {
        return this.store.state.auth as AuthState;
    }

    private get lastActivity(): number
    {
        return Number(localStorage.getItem("last-activity") || 0);
    }

    private set lastActivity(value: number)
    {
        localStorage.setItem("last-activity", value.toString());
    }

    private get accessToken(): string
    {
        return localStorage.getItem("access-token") || '';
    }

    private set accessToken(token: string)
    {
        localStorage.setItem("access-token", token);
    }

    private get refreshToken(): string
    {
        return localStorage.getItem("refresh-token") || '';
    }

    private set refreshToken(token: string)
    {
        localStorage.setItem("refresh-token", token);
    }

    private decryptToken(token: string): any
    {
        let base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        let payload = JSON.parse(window.atob(base64));

        return payload;
    }

    private setTokens(tokens: TokenModel, rememberMe: boolean): void
    {
        if (rememberMe === true)
        {
            this.refreshToken = tokens.refresh;
        }

        if (tokens.token.length > 0)
        {
            this.accessToken = tokens.token;

            let payload = this.decryptToken(tokens.token);
            let timeLeft = Number(payload.exp) * 1000  - (new Date()).getTime();

            if (timeLeft > 0)
            {
                setTimeout(async () =>
                {
                    try
                    {
                        let result = rememberMe ?
                            await AuthService.recoverToken(this.refreshToken) :
                            await AuthService.refreshToken();

                        this.setTokens(result, rememberMe);
                    }
                    catch (ex)
                    {
                        this.logout();
                    }
                },
                timeLeft);
            }
        }
    }

    private clearAccessToken(): void
    {
        localStorage.removeItem("access-token");
    }

    private clearRefreshToken(): void
    {
        localStorage.removeItem("refresh-token");
    }

    private clearTokens(): void
    {
        this.unimpersonate();
        this.clearAccessToken();
        this.clearRefreshToken();
    }

    private clearTimer(): void
    {
        localStorage.removeItem("last-activity");
    }

    public async login(username: string, password: string, rememberMe: boolean = false, postLoginCheck = (token: any) => true): Promise<void>
    {
        // console.log('1');
        try
        {
            this.clearTokens();

            let result = await AuthService.login(username, password);
            let token = this.decryptToken(result.token);

            if (postLoginCheck(token))
            {
                this.setTokens(result, rememberMe);
                await this.fetch();

                this.router.push(this.options.routes.homePage).catch(() => {});
            }
            else
            {
                throw {
                    code: 400,
                    message: "[[[E-post eller passord er feil.]]]",
                    data: null as any,
                    inner: null as any
                };
            }
        }
        catch (ex)
        {
            this.logout();
            throw ex;
        }
    }

    public logout(redirect?: Location): void
    {
        this.store.commit('auth/setAuthenticated', false);
        this.store.commit('auth/clearIdentity');
        this.clearTokens();
        this.clearTimer();

        this.router.push(redirect || this.options.routes.loginPage).catch(() => {});
    }

    public token(): string
    {
        return this.accessToken;
    }

    public async fetch(): Promise<void>
    {
        try
        {
            let result = await AuthService.getIdentity(true);
            this.store.commit('auth/setIdentity', result);
            this.store.commit('auth/setAuthenticated', true);
        }
        catch (ex)
        {
            this.store.commit('auth/clearIdentity');
            this.store.commit('auth/setAuthenticated', false);
        }
    }

    public user(): AuthModel
    {
        return this.state.identity;
    }

    public check(): boolean
    {
        return this.state.authenticated;
    }

    public ready(): boolean
    {
        return this.state.loaded;
    }

    public reload(): void
    {
        this.store.commit('auth/setLoaded', false);
        setTimeout(() => this.store.commit('auth/setLoaded', true), 100);
    }

    public impersonating(): number
    {
        return this.state.impersonated as number;
    }

    public impersonate(id: number): void
    {
        this.store.commit('auth/impersonate', id);
        localStorage.setItem("impersonated", id.toString());
    }

    public unimpersonate(): void
    {
        this.store.commit('auth/unimpersonate');
        localStorage.removeItem("impersonated");
    }
}

export const AuthPlugin: PluginObject<AuthOptions> =
{
    install(Vue, options)
    {
        if (!Vue.router)
        {
            throw new Error("Vue.router must be set.");
        }
        if (!options || !options.store)
        {
            throw new Error("AuthOptions.store must be set.");
        }

        Vue.auth = Vue.prototype.$auth = new AuthHelper(Vue.router, options);
    }
};

declare module 'vue/types/vue'
{
    interface Vue
    {
        $auth: Auth;
    }
    interface VueConstructor
    {
        auth: Auth;
    }
}
